import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { COMPANY, SERVICE_CITIES } from "../lib/constants";
import { PageHero, SectionHeading } from "../components/common";

const empty = { name: "", phone: "", email: "", inquiry_type: "residential", message: "", website: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please complete name, email and message."); return; }
    setSending(true);
    try {
      await api.post("/contacts", form);
      setDone(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (e) { toast.error(apiErr(e)); }
    setSending(false);
  };

  const inputCls = "w-full border border-gray-300 rounded-md px-4 py-2.5 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors";
  const labelCls = "block text-sm font-semibold text-charcoal-700 mb-1.5";

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Let's talk about your cleaning needs"
        subtitle="Have a question or ready to get started? Reach out and our Northwest Arkansas team will be happy to help." />

      <section className="bg-white">
        <div className="ubm-container py-20 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6">
            <SectionHeading eyebrow="Get in touch" title="We're here to help" />
            {[
              { icon: Phone, label: "Phone", val: COMPANY.phoneDisplay, href: COMPANY.phoneLink },
              { icon: Mail, label: "Email", val: COMPANY.email, href: COMPANY.emailLink },
              { icon: MapPin, label: "Service Area", val: COMPANY.region },
            ].map((c) => (
              <div key={c.label} className="flex items-start gap-3 border border-gray-200 rounded-md p-4">
                <c.icon className="text-primary shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{c.label}</div>
                  {c.href ? <a href={c.href} className="text-charcoal-900 font-medium hover:text-primary break-all">{c.val}</a> : <div className="text-charcoal-900 font-medium">{c.val}</div>}
                </div>
              </div>
            ))}
            <div className="border border-gray-200 rounded-md p-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2"><Clock size={14} className="text-primary" /> Business Hours</div>
              {COMPANY.hours.map((h) => (
                <div key={h.day} className="flex justify-between text-sm text-charcoal-700 py-0.5"><span>{h.day}</span><span className="font-medium">{h.time}</span></div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/commercial#quote" className="flex-1 text-center border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white font-semibold px-4 py-2.5 rounded-md transition-colors text-sm" data-testid="contact-quote-btn">Request a Commercial Quote</Link>
              <Link to="/book" className="flex-1 text-center bg-primary text-white hover:bg-primary-hover font-semibold px-4 py-2.5 rounded-md transition-colors text-sm" data-testid="contact-book-btn">Book a Residential Cleaning</Link>
            </div>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="bg-white border border-primary/30 rounded-md p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="text-primary mx-auto mb-4" size={48} />
                <h3 className="text-2xl font-bold text-charcoal-900">Message sent!</h3>
                <p className="mt-2 text-charcoal-600">Thanks for reaching out. We'll respond as soon as possible.</p>
                <button onClick={() => { setForm(empty); setDone(false); }} className="mt-6 border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white font-semibold px-6 py-2.5 rounded-md transition-colors">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-[#f8f9fa] border border-gray-200 rounded-md p-6 sm:p-8 space-y-5" data-testid="contact-form">
                <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Name *</label><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="c-name" required /></div>
                  <div><label className={labelCls}>Phone</label><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="c-phone" /></div>
                </div>
                <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="c-email" required /></div>
                <div>
                  <label className={labelCls}>Inquiry Type</label>
                  <select className={inputCls} value={form.inquiry_type} onChange={(e) => set("inquiry_type", e.target.value)} data-testid="c-type">
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div><label className={labelCls}>Message *</label><textarea rows={5} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="c-message" required /></div>
                <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-hover font-semibold px-6 py-3.5 rounded-md transition-colors disabled:opacity-60" data-testid="c-submit">
                  {sending ? "Sending…" : <>Send Message <ArrowRight size={18} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
